/** A text label pinned to a world point. */
export interface ViewerLabel {
  /** Stable id, so a label keeps its element between frames. */
  id: string;
  text: string;
  point: readonly [number, number, number];
  kind: 'measurement' | 'marker';
}

/**
 * Text labels over the 3D view: measurement values and marker names drawn as HTML at the screen position of their
 * world point. Positions update every frame without Angular change detection; labels behind the camera hide.
 */
export class ViewerLabels {
  private readonly elements: Map<string, HTMLElement> = new Map();
  private labels: readonly ViewerLabel[] = [];

  public constructor(
    private readonly container: HTMLElement,
    private readonly project: (point: readonly [number, number, number]) => { x: number; y: number } | null
  ) {}

  /** Replaces the labels; elements of labels that went away are removed. */
  public set(labels: readonly ViewerLabel[]): void {
    this.labels = labels;
    const ids: Set<string> = new Set(labels.map((label: ViewerLabel) => label.id));
    this.elements.forEach((element: HTMLElement, id: string) => {
      if (!ids.has(id)) {
        element.remove();
        this.elements.delete(id);
      }
    });
    for (const label of labels) {
      let element: HTMLElement | undefined = this.elements.get(label.id);
      if (element === undefined) {
        element = this.container.ownerDocument.createElement('div');
        this.container.appendChild(element);
        this.elements.set(label.id, element);
      }
      element.className = `viewer-label viewer-label--${label.kind}`;
      element.textContent = label.text;
    }
    this.update();
  }

  /** Moves every label to the current screen position of its point. */
  public update(): void {
    for (const label of this.labels) {
      const element: HTMLElement | undefined = this.elements.get(label.id);
      const screen: { x: number; y: number } | null = this.project(label.point);
      if (element === undefined) {
        continue;
      }
      element.hidden = screen === null;
      if (screen !== null) {
        element.style.transform = `translate(${Math.round(screen.x)}px, ${Math.round(screen.y)}px)`;
      }
    }
  }

  public clear(): void {
    this.set([]);
  }
}
